<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import PreviewModal from './PreviewModal.vue';
import PreviewForm, { type PreviewFormValues } from './PreviewForm.vue';
import { useMutation } from '@vue/apollo-composable';
import { graphql } from '@/gql';
import { useNotificationStore } from '@/stores/notification';

const showDialog = defineModel<boolean>({ default: false });
const { projectId } = defineProps<{ projectId: string }>();

const { t } = useI18n({
    messages: {
        nl: {
            title: '@:preview aanmaken',
            create_success: '@:preview succesvol aangemaakt',
        },
        en: {
            title: 'Create @.lower:preview',
            create_success: '@:preview successfully created',
        },
    },
});

const notificationStore = useNotificationStore();

const { mutate: createPreview, loading: isCreating } = useMutation(
    graphql(`
        mutation CreatePreview($input: CreatePreviewInput!) {
            createPreview(input: $input) {
                preview {
                    id
                }
            }
        }
    `),
    {
        refetchQueries: ['ProjectPreviews'],
        awaitRefetchQueries: true,
    }
);

async function handleCreate(values: PreviewFormValues) {
    try {
        await createPreview({
            input: { projectId, ...values },
        });

        showDialog.value = false;
        notificationStore.add({
            type: 'success',
            message: t('create_success'),
        });
    } catch {
        notificationStore.add({ type: 'error', message: t('create_error') });
    }
}
</script>

<template>
    <PreviewModal v-model="showDialog" :title="t('title')">
        <template #activator="{ props: activatorProps }">
            <slot name="activator" :props="activatorProps" />
        </template>
        <PreviewForm
            :disabled="isCreating"
            :loading="isCreating"
            @cancel="showDialog = false"
            @submit="handleCreate"
        />
    </PreviewModal>
</template>
